import { useCallback, useState, useContext, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  Typography,
  useTheme,
  Chip,
  CircularProgress,
} from "@mui/material";
import TagsList from "./TagsList.js";
import { AutoFixHigh, StarBorderOutlined, Star } from "@mui/icons-material";
import { ExplainerTooltip } from "src/components/upgrades/ExplainerTooltip.js";
import { useAddTags, useRemoveTags, getPageRoute } from "src/services/index.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { useNavigate } from "react-router-dom";
import { useDimensions } from "src/utils/useDimensions.js";
import SignInDialog from "src/components/blocks/dialogs/SignInDialog.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { useMutation } from "@tanstack/react-query";
import { keyframes } from "@emotion/react";
import useFadeSticky from "src/utils/useFadeSticky.js";

const Rectangle = ({ sx = {}, onClick }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "block",
        width: "26px",
        height: "6px",
        backgroundColor: "#79747E",
        opacity: 0.2,
        borderRadius: "100px",
        margin: "auto",
        transform: { xs: "translateX(-30%)", md: "translateX(0%)" },
        ...sx,
      }}
    />
  );
};

const SelectedTag = ({
  tag,
  isTagSaved,
  onSave,
  isLoadingTagSave,
  isCustomTag,
}) => {
  const [showTooltip, setShowTooltip] = useState(isCustomTag);

  useEffect(() => {
    setShowTooltip(isCustomTag);
  }, [isCustomTag]);

  useEffect(() => {
    const handleScroll = () => {
      if (showTooltip) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [showTooltip]);

  const handleSave = () => {
    setShowTooltip(false); // Close tooltip when saving
    onSave();
  };

  const chipContent = (
    <Chip
      label={tag}
      onDelete={!isLoadingTagSave ? handleSave : () => {}}
      deleteIcon={
        isLoadingTagSave ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress
              as="div"
              color="secondary"
              size={20}
              thickness={4}
            />
          </Box>
        ) : isTagSaved ? (
          <Star
            sx={{
              fontSize: "1.25rem !important",
              fill: "white !important",
              color: "white !important",
              stroke: "white",
              strokeWidth: "0.3px",
              "&:hover": {
                color: "white !important",
                fill: "white !important",
              },
            }}
          />
        ) : (
          <StarBorderOutlined
            sx={{
              fontSize: "1.25rem !important",
              stroke: "white",
              strokeWidth: "0.3px",
            }}
          />
        )
      }
      color="primary"
      sx={{
        height: "36px",
        fontSize: "14px",
        borderRadius: "30px",
        pl: "10px",
        fontWeight: 400,
        "& span": {
          textAlign: "center",
          minWidth: "95px",
        },
      }}
    />
  );

  return (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        justifyContent: "flex-start",
      }}
    >
      {isCustomTag && showTooltip ? (
        <ExplainerTooltip
          explainer="Star this tag if you want to save it."
          forceOpen={true}
        >
          {chipContent}
        </ExplainerTooltip>
      ) : (
        chipContent
      )}
    </Box>
  );
};

const TagsCollection = ({
  trendingCalendarTags,
  setTagsFilter,
  tagsFilter,
  savedTags,
  isLoadingCalendarsByTags,
  activeTab,
  manageCustomTags,
  urlTag,
}) => {
  const { mutateAsync: addTags } = useAddTags();
  const { mutateAsync: removeTags } = useRemoveTags();
  const navigate = useNavigate();
  const { user, refetchAuth } = useContext(AuthContext);
  const [isSelectingTags, setIsSelectingTags] = useState(true);
  const { isMediumScreen } = useDimensions();
  const theme = useTheme();
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const [isAnimating, setIsAnimating] = useState(false);
  const stickyRef = useFadeSticky({ calculateTopFromTabs: true });

  const glow = keyframes`
    0% {
      opacity: 1;
      color: ${theme.palette.text.primary};
    }
    50% {
      opacity: 0.7;
      color: ${theme.palette.primary.main};
      transform: rotate(20deg);
    }
    100% {
      opacity: 1;
      color: ${theme.palette.text.primary};
      transform: rotate(0deg);
    }
  `;

  const isCurrentTagSaved = useMemo(() => {
    return savedTags?.includes(tagsFilter[0]);
  }, [savedTags, tagsFilter]);

  const isCurrentTagCustom = useMemo(() => {
    return (
      tagsFilter[0] &&
      manageCustomTags &&
      trendingCalendarTags &&
      !trendingCalendarTags.includes(tagsFilter[0])
    );
  }, [manageCustomTags, tagsFilter, trendingCalendarTags]);

  useEffect(() => {
    if (user && isLoadingCalendarsByTags && activeTab === "discover") {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [isLoadingCalendarsByTags]);

  const handleTagClick = useCallback(
    (tags, action, tag) => {
      if (action === "change") {
        setTagsFilter([tag]);
        if (activeTab !== "foryou") {
          navigate(getPageRoute("home", "TAG", { tag }));
        }
      }

      customEvent({
        name: `${action}_home_tag`,
        category: "HomeCalendarTags",
        label: `User or guest ${action} home tag`,
        data: {
          user_id: user?._id ?? "guest",
          user_email: user?.email ?? "-",
          user_handle: user?.handle ?? "no_handle",
          tag,
        },
      });
    },
    [navigate, user, setTagsFilter, activeTab]
  );

  const toggleIsSelectingTags = () => {
    setIsSelectingTags(!isSelectingTags);
  };

  const { mutateAsync: handleTagSave, isLoading: isLoadingTagSave } =
    useMutation(async () => {
      if (!user) {
        setOpenSignInDialog(true);
        return;
      }

      try {
        const tag = tagsFilter[0];
        const newSavedState = !isCurrentTagSaved;

        if (newSavedState) {
          await addTags({ tags: [tag] });
        } else {
          await removeTags({ tags: [tag] });
        }

        await refetchAuth();

        customEvent({
          name: newSavedState ? "save_home_tag" : "unsave_home_tag",
          category: "HomeCalendarTags",
          label: newSavedState
            ? "User saved home tag"
            : "User unsaved home tag",
          data: {
            user_id: user?._id ?? "guest",
            user_email: user?.email ?? "-",
            user_handle: user?.handle ?? "no_handle",
            tag,
          },
        });

        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          `Tag ${newSavedState ? "saved" : "unsaved"} successfully.`,
          "success"
        );
      } catch (error) {
        refetchAuth();
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          "⚠️ Error saving. Please try again.",
          "error"
        );
      }
    });

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 3000);
    }, 1000);

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 3000);
    }, 15000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <Box
        ref={stickyRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          ...(!isMediumScreen
            ? {
                width: "100%",
                padding: "1rem 0 0 0",
                ml: "0rem",
              }
            : {
                width: "calc(100% + 1rem)",
                padding: `${isSelectingTags ? "1" : "0.3"}rem 0 0 1rem`,
                ml: "-1rem",
              }),
          zIndex: "10",
          background: "#ffffff",
        }}
      >
        {!isMediumScreen && (
          <Box
            sx={{
              position: "absolute",
              top: "0%",
              left: "-10%",
              width: "120%",
              height: "97%",
              background: "#ffffff",
            }}
          />
        )}

        <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
          <TagsList
            tags={trendingCalendarTags}
            tagsFilter={tagsFilter}
            setTagsFilter={setTagsFilter}
            isSelectingTags={isSelectingTags}
            isLoading={trendingCalendarTags.length === 0}
            multipleSelect={false}
            rows={2}
            slideArrows={true}
            onTagClick={handleTagClick}
            sx={{ "& .MuiChip-label": { fontSize: "13px" } }}
          />

          <Button
            component="div"
            onClick={() => {
              customEvent({
                name: "user_click_personalize_button",
                category: "HomeCalendarTags",
                label: "User click Personalize button on Home page",
                data: {
                  user_id: user?._id ?? "guest",
                  user_email: user?.email ?? "-",
                  user_handle: user?.handle ?? "no_handle",
                },
              });
              navigate(getPageRoute("home", "PERSONALIZE"));
            }}
            sx={{
              textTransform: "none",
              fontSize: "inherit",
              display: "block",
              width: "fit-content",
              overflow: "hidden",
              maxHeight: isSelectingTags ? "999px" : "0px",
              transition: "all ease 300ms",
              opacity: isSelectingTags ? "1" : "0",
              my: isSelectingTags ? 1.5 : 0,
              pointerEvents: isSelectingTags ? "auto" : "none",
              ...(!isMediumScreen
                ? { mx: "auto" }
                : { mx: "0", alignSelf: "center", ml: "-1rem" }),
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "self-end",
                gap: 1,
              }}
            >
              <AutoFixHigh
                fontSize="small"
                sx={{
                  animation: isAnimating ? `${glow} 3s ease-in-out` : "none",
                  transformOrigin: "center",
                  color: theme.palette.text.primary,
                }}
              />
              <Typography variant="subtitle1" color="text.primary">
                Personalize
              </Typography>
            </Box>
          </Button>

          <Rectangle
            onClick={toggleIsSelectingTags}
            sx={{ cursor: "pointer" }}
          />
          {/* Selected Tag */}
          {tagsFilter[0] && (
            <SelectedTag
              tag={tagsFilter[0]}
              isTagSaved={isCurrentTagSaved}
              onSave={handleTagSave}
              sx={{ textAlign: "left" }}
              isLoadingTagSave={isLoadingTagSave}
              isCustomTag={isCurrentTagCustom}
            />
          )}
        </Box>

        {/* TODO: Add back in when we have related tags */}
        {/* {relatedTags?.length > 0 || isLoadingCalendarsByTags ? (
          <>
            <Typography variant="h2" sx={{ mt: 3, mb: 2 }}>
              Related Tags
            </Typography>
            <TagsList
              tags={relatedTags}
              tagsFilter={tagsFilter}
              setTagsFilter={setTagsFilter}
              multipleSelect={true}
              isLoading={isLoadingCalendarsByTags}
              isSelectingTags={true}
              rows={2}
              onTagClick={handleTagClick}
              sx={{ mb: 2 }}
            />
          </>
        ) : (
          <Box sx={{ mb: 3 }} />
        )} */}

        <Divider
          sx={{
            ...(!isMediumScreen
              ? { width: "100%", ml: "0rem" }
              : { width: "calc(100% + 1rem)", ml: "-1rem" }),
            mt: 1,
            mb: 0,
          }}
        />
      </Box>
      <SignInDialog
        open={openSignInDialog}
        handleClose={() => setOpenSignInDialog(false)}
        onSignIn={async () => {
          await handleTagSave();
        }}
      />
    </>
  );
};

export default TagsCollection;
